import React, { useCallback, useContext, useEffect, useRef, useState } from "react"
import { View, ScrollView, StyleSheet, Text, Image } from "react-native"
import { useFocusEffect } from "@react-navigation/native"
import * as amplitude from "@amplitude/analytics-react-native"
import FilledButtonM from "@common/components/button/FilledButtonM"
import { theme } from "@common/theme/theme"
import {
    FUNNEL_TITLE_FONT_SIZE,
    FUNNEL_TITLE_LINE_HEIGHT,
} from "@common/components/funnel/FunnelQuestionScreen"
import { DimensionsContext } from "@common/utils/mobileUtils"
import revealIllustration from "@assets/summer/funnel/reveal.png"
import BrevetFunnelHeader from "../brevet/components/BrevetFunnelHeader"
import { useSummerFunnel } from "./context/SummerFunnelContext"
import { useSummerFunnelGuard } from "./hooks/useSummerFunnelGuard"
import { summerFunnelService } from "./services/summerFunnelService"
import {
    SUMMER_DEFAULT_SLOT_IDS,
    weeklyHoursFromSlots,
} from "./constants/summerContent"
import SummerProgramCalendar from "./components/SummerProgramCalendar"
import SummerSubjectHoursModal from "./components/SummerSubjectHoursModal"
import SummerEyebrow from "./components/SummerEyebrow"
import SummerHelpBanner from "./components/SummerHelpBanner"

/**
 * Étape "programme d'été" (REVEAL de valeur) : programme personnalisé planifié,
 * généré par l'API. Vient APRÈS le configurateur — donc les CRÉNEAUX réels sont
 * connus et le programme est généré sur le VRAI calendrier (dates de séances
 * réelles), pas un placeholder. Le contenu dépend des matières + niveau +
 * objectif + classe ; les créneaux pilotent la planification (dates / cadence).
 *
 * C'est le moment de payoff : la valeur perçue est montrée juste AVANT le
 * checkout 1 €. Cet écran ne s'affiche qu'une fois la formule complète (matières
 * + diagnostic + créneaux) ; si le payload programme est incomplet on renvoie au
 * configurateur plutôt que de tirer un appel qui finirait en 422.
 *
 * Visuel : header allégé + petite illustration de reveal + pill « ☀️ Cours d'été »,
 * même langage que le reste du funnel. Le calendrier (sous-titre, légende
 * matières, semaines, bandeau réassurance) est délégué à <SummerProgramCalendar />,
 * partagé avec la modale d'aperçu du checkout. Responsive : colonne centrée en
 * large desktop.
 */

// Même largeur de colonne que les écrans de question (F2 : homogénéité).
const CONTENT_MAX = 480
// Heures/sem par défaut si — cas limite — les créneaux ne sont pas encore en
// state (accès direct au reveal sans passer par le configurateur). Le cas normal
// envoie les créneaux choisis au configurateur juste avant.
const REVEAL_FALLBACK_HOURS_PER_WEEK = 3

const SummerPlanScreen = ({ navigation }) => {
    const dimensions = useContext(DimensionsContext)
    const { state, update } = useSummerFunnel()
    // Refresh-safe / URL-partageable (voir useSummerFunnelGuard).
    const { nextParams } = useSummerFunnelGuard()

    const [loading, setLoading] = useState(true)
    const [program, setProgram] = useState(null)
    const [failed, setFailed] = useState(false)
    // Modale (option power user) : répartir le pack d'heures entre les matières.
    const [hoursModalVisible, setHoursModalVisible] = useState(false)

    // Créneaux choisis au configurateur (fallback : les 3 par défaut si — cas
    // limite — l'écran est atteint sans passer par le configurateur). Le program
    // endpoint exige slot_ids ; sans eux il renverrait un 422 → placeholder.
    const slotIds = state.selectedSlotIds?.length
        ? state.selectedSlotIds
        : SUMMER_DEFAULT_SLOT_IDS
    // Pack d'heures hebdo = nombre de créneaux choisis (chaque créneau = 1 h).
    const weeklyHours = weeklyHoursFromSlots(slotIds)

    // Enregistre la répartition heures/matière choisie dans la modale → écrit
    // `subjectHours` dans le contexte (la signature change → re-fetch du programme
    // avec la nouvelle allocation, honorée par le backend quand cohérente).
    const onSaveSubjectHours = (allocation) => {
        update({ subjectHours: allocation })
        setHoursModalVisible(false)
    }

    // Payload complet requis par le program endpoint (sinon 422) : matières +
    // objectif + niveau + classe + classe rentrée + créneaux + un id d'état valide.
    // Les créneaux ont un fallback (3 par défaut) → jamais bloquants ; les autres
    // champs viennent de la classe (start) et du diagnostic et doivent être présents.
    //
    // `hasValidStateId` : sur contexte froid (refresh / lien magique pas encore
    // hydraté), l'id peut être null/undefined → l'appel partait sur
    // `/summer-funnel/undefined/program` (500). On attend l'hydratation : la garde
    // `useSummerFunnelGuard` route alors vers `summer/resume`.
    const funnelStateId = Number(state.funnelStateId)
    const hasValidStateId = Number.isInteger(funnelStateId) && funnelStateId > 0
    // Diagnostic (objectif + niveau) + classes, collectés en amont (start +
    // diagnostic). Les matières sont choisies au configurateur.
    const hasDiagnostic =
        !!state.objective &&
        !!state.niveauActuel &&
        !!state.studentClass &&
        !!state.studentClassRentree
    const hasSubjects = (state.subjects?.length || 0) > 0
    const hasProgramPayload =
        hasValidStateId && hasDiagnostic && hasSubjects && slotIds.length > 0

    // Première étape amont capable de compléter un payload partiel. Sert à NE
    // JAMAIS rester bloqué sur le reveal (spinner infini) quand un champ manque —
    // typiquement un lead pré-migration repris sans objectif/niveau dans
    // `checkout_params`. On route vers l'étape qui collecte la donnée manquante,
    // qui la repersiste (updateAnswers / start) → le reveal recharge ensuite
    // normalement (data self-heal). `null` quand le payload est complet.
    const incompleteStepRoute =
        !state.studentClass || !state.studentClassRentree
            ? "summer/start"
            : !state.objective || !state.niveauActuel
              ? "summer/diagnostic"
              : !hasSubjects
                ? "summer/configurator"
                : null

    useEffect(() => {
        amplitude.track("summer_funnel_plan_viewed", { funnel_state_id: state.funnelStateId })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Signature des entrées qui pilotent le programme. Sert à re-générer le reveal
    // quand le parent revient au configurateur, modifie la formule, puis re-clique
    // « Voir le programme » : l'écran reveal est déjà monté (back-nav ne re-monte
    // pas), donc on rafraîchit au focus dès que la signature change (ou qu'un essai
    // précédent a échoué). Garantit « Voir le programme marche à chaque fois ».
    const programSignature = JSON.stringify({
        subjects: state.subjects,
        objective: state.objective,
        niveauActuel: state.niveauActuel,
        studentClass: state.studentClass,
        studentClassRentree: state.studentClassRentree,
        slotIds,
        subjectHours: state.subjectHours,
        months: state.selectedMonths,
        startDate: state.startDate,
    })
    const lastFetchedSignatureRef = useRef(null)

    useFocusEffect(
        useCallback(() => {
            // Contexte froid (reprise/refresh non hydratée) : l'id d'état n'est pas
            // encore là → la garde route vers `summer/resume`. On attend sans rien
            // tirer ni rediriger (le spinner reflète la reprise en cours).
            if (!hasValidStateId) return
            // Hydraté mais payload partiel (ex. lead pré-migration sans objectif/
            // niveau) : on route vers la première étape capable de compléter, AU
            // FOCUS (pas seulement au mount) pour couvrir le back-nav sur un écran
            // déjà monté. Jamais de spinner infini : si aucune étape n'est
            // identifiable (cas théorique), on coupe au moins le loader.
            if (!hasProgramPayload) {
                if (incompleteStepRoute) {
                    navigation.navigate(incompleteStepRoute, nextParams)
                } else {
                    setLoading(false)
                }
                return
            }
            // Évite un re-fetch inutile si rien n'a changé depuis le dernier appel
            // réussi (mémoïsé backend, mais on s'épargne l'aller-retour réseau).
            if (lastFetchedSignatureRef.current === programSignature && !failed) {
                return
            }
            lastFetchedSignatureRef.current = programSignature
            let cancelled = false
            setLoading(true)
            setFailed(false)
            summerFunnelService
                .getProgram(
                    { stateId: funnelStateId },
                    {
                        subjects: state.subjects,
                        objective: state.objective,
                        niveauActuel: state.niveauActuel,
                        studentClass: state.studentClass,
                        studentClassRentree: state.studentClassRentree,
                        // Créneaux RÉELS choisis au configurateur → vrai calendrier.
                        // hours_per_week = pack d'heures (somme des durées) ; le
                        // backend le re-dérive de slot_ids de toute façon.
                        hoursPerWeek:
                            weeklyHours || REVEAL_FALLBACK_HOURS_PER_WEEK,
                        slotIds,
                        // Répartition heures/matière (optionnelle) — honorée si
                        // cohérente, sinon split auto côté backend.
                        subjectHours: state.subjectHours,
                        months: state.selectedMonths,
                        // Date de démarrage choisie au configurateur — décale la
                        // 1ʳᵉ séance / le calendrier du programme.
                        startDate: state.startDate,
                    }
                )
                .then((res) => {
                    if (cancelled) return
                    const payload = res?.data?.data ?? res?.data ?? null
                    setProgram(payload)
                    if (payload?.first_session) {
                        update({ firstSession: payload.first_session })
                    }
                })
                .catch(() => {
                    if (cancelled) return
                    setFailed(true)
                    // Permet un retry au prochain focus (la signature n'est plus
                    // considérée comme « déjà chargée avec succès »).
                    lastFetchedSignatureRef.current = null
                })
                .finally(() => {
                    if (!cancelled) setLoading(false)
                })
            return () => {
                cancelled = true
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [hasValidStateId, hasProgramPayload, incompleteStepRoute, programSignature])
    )

    const containerWidth = Math.min(dimensions?.width || 400, CONTENT_MAX)
    const firstname = state.studentFirstname || "votre enfant"

    return (
        <View style={styles.root}>
            <View style={[styles.frame, { width: containerWidth }]}>
                <BrevetFunnelHeader
                    step={5}
                    total={7}
                    hideLogo
                    onBack={() => navigation.goBack()}
                />
                <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
                    <View style={styles.illustrationBand}>
                        <Image
                            source={revealIllustration}
                            style={styles.illustration}
                            resizeMode="contain"
                        />
                    </View>
                    <SummerEyebrow />
                    <Text style={styles.title}>Le programme d'été de {firstname}</Text>

                    <SummerProgramCalendar
                        program={program}
                        firstname={firstname}
                        loading={loading}
                        failed={failed}
                        onEditHours={
                            (program?.subjects?.length || 0) > 1
                                ? () => setHoursModalVisible(true)
                                : undefined
                        }
                    />
                    <SummerHelpBanner step={5} />
                </ScrollView>
                <SummerSubjectHoursModal
                    isVisible={hoursModalVisible}
                    onClose={() => setHoursModalVisible(false)}
                    subjects={program?.subjects || []}
                    weeklyHours={program?.hours_per_week || weeklyHours}
                    initialAllocation={state.subjectHours}
                    onSave={onSaveSubjectHours}
                />
                <View style={styles.footer}>
                    <FilledButtonM
                        label="Continuer"
                        onPress={() =>
                            navigation.navigate("summer/tutors", nextParams)
                        }
                        // Désactivé tant que le programme n'a pas chargé : empêche de
                        // filer au checkout avant que l'écriture programme (matières /
                        // heures) ait atterri. Sinon une écriture concurrente
                        // (create-checkout-session au montage du checkout) peut
                        // l'écraser (lost-update sur checkout_params).
                        disabled={loading}
                        color={
                            loading
                                ? theme.colors.lightMore
                                : theme.colors.primary
                        }
                        style={{ width: "100%", marginBottom: 0 }}
                    />
                </View>
            </View>
        </View>
    )
}

const styles = StyleSheet.create({
    root: { flex: 1, alignItems: "center", backgroundColor: theme.colors.whiteAlmost },
    frame: { flex: 1, backgroundColor: theme.colors.white, paddingHorizontal: 20 },
    content: { paddingTop: 14, paddingBottom: 24 },
    illustrationBand: {
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: theme.colors.primaryLighter,
        borderRadius: 16,
        paddingVertical: 18,
        marginBottom: 16,
    },
    illustration: { width: 132, height: 132 },
    title: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: FUNNEL_TITLE_FONT_SIZE,
        lineHeight: FUNNEL_TITLE_LINE_HEIGHT,
        color: theme.colors.primaryDark || theme.colors.primary,
        marginBottom: 12,
    },
    footer: {
        paddingVertical: 14,
        borderTopWidth: 1,
        borderTopColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
    },
})

export default SummerPlanScreen
